import { logReportDownload } from '../../util/auditLogger';
import React, { useState, useCallback } from "react";
import { useToast } from "../../components/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { Header } from "../../components/modules";
import { FiFileText } from "react-icons/fi";
import { MdPersonSearch } from "react-icons/md";
import { Spinner } from "reactstrap";
import * as xlsx from "xlsx";
import Context from "../../Context";
import { statuses } from "../../constants";
import constant from "../../constants";
import { thaiDate } from "../../util";
import dayjs from "dayjs";
import buddhistEra from "dayjs/plugin/buddhistEra";
import dayTh from "dayjs/locale/th";

dayjs.extend(buddhistEra);
dayjs.locale(dayTh);

// ============================================
// Constants
// ============================================
const TICKET_PRICE = 80;
const TAX_DIVISOR = 100 / 107;
const API_LIMIT = 1000;

const DELIVERY_CHANNELS = {
  online: "1. ฝากไว้กับวันดี",
  mail: "2. ส่งไปรษณีย์",
  pickup: "3. มารับเอง 80 บาท",
};

const CSV_HEADERS = [
  "ลำดับ",
  "เลขที่ใบกำกับภาษี",
  "วันที่อนุมัติ",
  "เวลาที่อนุมัติ",
  "รหัสคำสั่งซื้อ",
  "ช่องทางการสั่งซื้อ",
  "รหัสผู้ใช้",
  "ชื่อ-นามสกุล",
  "เบอร์โทรศัพท์",
  "ส่วนลด",
  "จำนวนสลาก",
  "ราคาสลาก",
  "ค่าบริการ",
  "มูลค่าก่อนภาษี",
  "ภาษี 7%",
  "ราคารวม",
  "สกุลเงิน",
  "ช่องทางการสั่งซื้อ",
  "สถานะ",
];

// ============================================
// Helper Functions
// ============================================
const formatThaiDate = (date) => {
  const d = new Date(date);
  return {
    date: d.toLocaleDateString("th-TH", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    }),
    time: d.toLocaleTimeString("th-TH", {
      hour: "numeric",
      minute: "numeric",
    }),
  };
};

const getDeliveryChannelText = (channel) =>
  DELIVERY_CHANNELS[channel] || "3. มารับเอง 80 บาท";

const isVoucherType = (coupon) =>
  coupon?.type === "voucher" ||
  coupon?.type === "voucher-lock";

const calculateOrderPricing = (order) => {
  console.log("🚀 ~ calculateOrderPricing ~ order:", order);
  if (order.totalPrice <= 0 || order.status === "canceled") {
    return {
      ticketPrice: "0",
      serviceFee: "0",
      preTaxValue: "0",
      tax: "0",
      totalPrice: "0",
    };
  }
  let calculateSize = order.size;
  if (isVoucherType(order.coupon?.coupon)) {
    calculateSize -= order.coupon.coupon.quantity || 0;
  }

  const ticketPrice = Math.min(order.totalPrice, calculateSize * TICKET_PRICE);
  const serviceFee = Math.max(0, order.totalPrice - ticketPrice);
  const preTaxValue = serviceFee * TAX_DIVISOR;
  const tax = serviceFee * (1 - TAX_DIVISOR);

  return {
    ticketPrice: ticketPrice.toLocaleString(),
    serviceFee: serviceFee.toLocaleString(),
    preTaxValue: preTaxValue.toFixed(2).toLocaleString(),
    tax: tax.toFixed(2).toLocaleString(),
    totalPrice: order.totalPrice.toLocaleString(),
  };
};

// ============================================
// Subcomponent: Search Panel
// ============================================
const TaxInvoiceSearchPanel = ({
  configs,
  selectedRoundDate,
  selectedPaymentChannel,
  loading,
  onRoundDateChange,
  onPaymentChannelChange,
  onSearch,
  onDownload,
}) => (
  <div className="relative w-96 bg-white rounded-lg shadow-md mt-4 border border-gray-300 overflow-hidden">
    <div className="p-3 border-b border-gray-300">
      <p className="font-semibold">รายงานใบกำกับภาษี</p>
    </div>
    <div className="relative grid grid-cols-2 items-center gap-3 p-3">
      {loading && (
        <div className="z-50 bg-[rgb(255,255,255,0.7)] absolute h-full w-full flex justify-center items-center">
          <Spinner className="w-4 h-4" />
        </div>
      )}
      <div className="gap-2 col-span-2">
        <p className="font-medium text-xs">งวด</p>
        <select
          className="border-2 w-full h-9 border-gray-300 rounded-lg cursor-pointer disabled:cursor-not-allowed disabled:opacity-65"
          value={selectedRoundDate}
          onChange={onRoundDateChange}
          disabled={loading}
        >
          {configs.roundDates?.map((roundDate) => (
            <option key={roundDate} value={roundDate}>
              {roundDate}
            </option>
          ))}
        </select>
      </div>
      <div className="gap-2 col-span-2">
        <p className="font-medium text-xs">รูปแบบการชำระ</p>
        <select
          className="border-1 w-full border-gray-300 h-9 rounded-lg cursor-pointer disabled:cursor-not-allowed disabled:opacity-65"
          value={selectedPaymentChannel}
          onChange={onPaymentChannelChange}
          disabled={loading}
        >
          <option value="">ทั้งหมด</option>
          <option value="qr">QR</option>
          <option value="bank">แนบสลิป</option>
        </select>
      </div>

      <button
        disabled={loading}
        className="flex items-center justify-center gap-1 py-2 px-3 bg-blue text-white text-sm font-semibold rounded-lg shadow-md hover:opacity-60 transition-all duration-200 disabled:opacity-60"
        onClick={onSearch}
        aria-label="ค้นหาคำสั่งซื้อ"
      >
        <MdPersonSearch className="text-white" />
        ค้นหา
      </button>

      <button
        disabled={loading}
        className="flex items-center justify-center gap-1 py-2 px-3 bg-[rgb(59,176,101)] text-white text-sm font-semibold rounded-lg shadow-md hover:opacity-60 transition-all duration-200 disabled:bg-slate-300 disabled:hover:opacity-100 disabled:cursor-not-allowed"
        onClick={onDownload}
        aria-label="ดาวโหลดรายงานภาษี"
      >
        {loading ? (
          <Spinner className="w-3 h-3" />
        ) : (
          <MdPersonSearch className="text-white" />
        )}
        ดาวโหลด
      </button>
    </div>
  </div>
);

export const ReportTaxInvoice = () => {
  const { toast } = useToast();
  const { callApi, configs } = Context();

  // State management
  const [orders, setOrders] = useState(false);
  const [selectedRoundDate, setSelectedRoundDate] = useState(
    configs?.roundDate || "",
  );
  const [selectedPaymentChannel, setSelectedPaymentChannel] = useState("");
  const [loading, setLoading] = useState(false);

  // ============================================
  // API Handlers
  // ============================================
  const fetchOrdersWithPagination = useCallback(
    async (queries, roundDate) => {
      const orderData = [];
      let keepGoing = true;
      let startAtId = "";

      while (keepGoing) {
        try {
          const data = await callApi(
            "orders-getAll",
            {
              queries,
              limit: API_LIMIT,
              startAfter: startAtId,
              roundDate,
              orderBy: ["approvedAt", "asc"],
            },
            { notShowLoading: true },
          );

          if (!data || data.length < API_LIMIT) {
            keepGoing = false;
          } else {
            startAtId = data[data.length - 1].id;
          }

          orderData.push(...data);
        } catch (error) {
          console.error("Error fetching orders:", error);
          // toast("Failed to fetch orders");
          keepGoing = false;
        }
      }

      return orderData;
    },
    [callApi, toast],
  );

  const getOrders = useCallback(async () => {
    try {
      setOrders(false);
      setLoading(true);

      const queries = [
        ["taxNo", "!=", "-"],
        // ["status", "==", statuses.APPROVE],
      ]; // Only fetch orders with taxNo and not canceled
      if (selectedPaymentChannel) {
        queries.push(["paymentChannel", selectedPaymentChannel]);
      }

      const orderData = await fetchOrdersWithPagination(
        queries,
        selectedRoundDate,
      );

      setOrders(
        orderData.filter(
          (x) =>
            x.totalPrice > 0 && (x.paidCurrency !== "LAK" || !x.paidCurrency),
        ),
      );
    } catch (error) {
      console.error("Error in getOrders:", error);
      // toast("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  }, [
    selectedRoundDate,
    selectedPaymentChannel,
    fetchOrdersWithPagination,
    toast,
  ]);

  const handleDownload = useCallback(() => {
    try {
      if (!orders || orders.length === 0) {
        // toast("No orders to download");
        return;
      }

      const data = [CSV_HEADERS];
      orders.forEach((order, index) => {
        const { date, time } = formatThaiDate(order.approvedAt);

        let calcSize = order.size;
        if (isVoucherType(order.coupon?.coupon)) {
          calcSize -= order.coupon.coupon.quantity || 0;
        }
        const isZero = order.totalPrice <= 0 || order.status === "canceled";
        const rawTicketPrice = isZero ? 0 : Math.min(Number(order.totalPrice), Number(calcSize) * TICKET_PRICE);
        const rawServiceFee = isZero
          ? 0
          : Math.max(0, Number(order.totalPrice) - rawTicketPrice);
        const rawPreTaxValue = isZero
          ? 0
          : parseFloat((rawServiceFee * TAX_DIVISOR).toFixed(2));
        const rawTax = isZero
          ? 0
          : parseFloat((rawServiceFee * (1 - TAX_DIVISOR)).toFixed(2));
        const rawTotalPrice = isZero ? 0 : Number(order.totalPrice);

        data.push([
          index + 1,
          order.taxNo || order.receiptNo || "-",
          date,
          time,
          order.shortId,
          getDeliveryChannelText(order.deliverChannel),
          order.userId,
          `${order.firstName || ""} ${order.lastName || ""}`,
          `~${order.phone || ""}`,
          order.coupon
            ? `${order.coupon?.coupon?.name} #${order.coupon.shortId}`
            : "-",
          Number(order.size),
          rawTicketPrice,
          rawServiceFee,
          rawPreTaxValue,
          rawTax,
          rawTotalPrice,
          order.paidCurrency || "-",
          order.paymentChannel || "-",
          order.status,
        ]);
      });

      const ws = xlsx.utils.aoa_to_sheet(data);
      const wb = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(wb, ws, "Sheet1");
      logReportDownload();
      xlsx.writeFile(
        wb,
        `รายงานใบกำกับภาษี_งวด_${selectedRoundDate}_ออก_ณ_วันที่${thaiDate()}.xlsx`,
      );
      // toast("Report generated successfully");
    } catch (error) {
      console.error("Error preparing download:", error);
      // toast("Failed to prepare download");
    }
  }, [orders, selectedRoundDate, toast]);

  return (
    <div className="h-fit w-full max-w-[1600px] mb-24">
      {/* Header */}
      <Header
        header="Report"
        subHeader="รายงานภาษี - สลาก"
        icon={<FiFileText className="text-blue" />}
      />

      {/* Search Panel */}
      <TaxInvoiceSearchPanel
        configs={configs}
        selectedRoundDate={selectedRoundDate}
        selectedPaymentChannel={selectedPaymentChannel}
        loading={loading}
        onRoundDateChange={(e) => setSelectedRoundDate(e.target.value)}
        onPaymentChannelChange={(e) =>
          setSelectedPaymentChannel(e.target.value)
        }
        onSearch={getOrders}
        onDownload={handleDownload}
      />

      {/* Results Table */}
      {orders && <ResultsTable orders={orders} />}
    </div>
  );
};

// ============================================
// Subcomponent: Results Table
// ============================================
const ResultsTable = ({ orders }) => {
  if (!orders || orders.length === 0) {
    return (
      <div className="relative bg-white w-full rounded-lg shadow-md mt-4 border border-gray-300 overflow-hidden">
        <div className="flex bg-white gap-3 items-center p-3 border-b border-gray-300">
          <p className="font-semibold">คำสั่งซื้อทั้งหมด</p>
          <p className="text-xs font-semibold text-[#969797]">0 คำสั่งซื้อ</p>
        </div>
        <div className="min-h-52 w-full flex justify-center items-center font-medium text-[#969797]">
          <div className="text-center">
            <MdPersonSearch className="text-white mx-auto mb-2" />
            ไม่พบคำสั่งซื้อ
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-white w-full rounded-lg shadow-md mt-4 border border-gray-300 overflow-hidden">
      <div className="flex bg-white gap-3 items-center p-3 border-b border-gray-300">
        <p className="font-semibold">คำสั่งซื้อทั้งหมด</p>
        <p className="text-xs font-semibold text-[#969797]">
          {orders.length.toLocaleString()} คำสั่งซื้อ
        </p>
      </div>

      <Table>
        <TableHeader className="bg-[#f2f3f5]">
          <TableRow>
            {/* <TableHead>ลำดับที่</TableHead> */}
            <TableHead>เลขที่ใบกำกับภาษี</TableHead>
            <TableHead>วันที่ - เวลาที่อนุมัติ</TableHead>
            <TableHead>รหัสคำสั่งซื้อ</TableHead>
            <TableHead>ช่องทางการซื้อ</TableHead>
            <TableHead className="text-right">รหัสผู้ใช้</TableHead>
            <TableHead>ชื่อ-นามสกุล</TableHead>
            <TableHead className="text-center">เบอร์โทรศัพท์</TableHead>
            <TableHead className="text-center">จำนวนสลาก</TableHead>
            <TableHead className="text-center">ส่วนลด</TableHead>
            <TableHead className="text-right">ราคาสลาก</TableHead>
            <TableHead className="text-right">ค่าบริการ</TableHead>
            <TableHead className="text-right">มูลค่าก่อนภาษี</TableHead>
            <TableHead className="text-right">ภาษี 7%</TableHead>
            <TableHead className="text-right">ราคารวม</TableHead>
            <TableHead>ช่องทางการชำระ</TableHead>
            <TableHead className="text-right">สถานะ</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {orders.reverse().map((order, index) => (
            <OrderRow
              key={order.id || order.shortId}
              order={order}
              index={index}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

// ============================================
// Subcomponent: Order Row
// ============================================
const OrderRow = ({ order, index }) => {
  const { date, time } = formatThaiDate(order.approvedAt);
  const pricing = calculateOrderPricing(order);

  return (
    <TableRow>
      {/* <TableCell className="font-semibold font-mono tabular-nums text-center">
        {index + 1}
      </TableCell> */}
      <TableCell className="font-semibold font-mono tabular-nums text-center">
        {order.taxNo || order.receiptNo || "-"}
      </TableCell>
      <TableCell className="font-medium">
        {date} {time} น.
      </TableCell>
      <TableCell className="font-semibold font-mono tabular-nums text-center">
        {order.shortId}
      </TableCell>
      <TableCell>
        <p>{getDeliveryChannelText(order.deliverChannel)}</p>
      </TableCell>
      <TableCell className="font-semibold text-right font-mono tabular-nums">
        {order.userId}
      </TableCell>
      <TableCell className="font-medium">
        {`${order.firstName || ""} ${order.lastName || ""}`}
      </TableCell>
      <TableCell className="font-medium text-center">
        {order.phone || "-"}
      </TableCell>
      <TableCell className="text-center font-mono tabular-nums">
        {order.size}
      </TableCell>
      <TableCell className="text-center font-mono tabular-nums">
        {order.coupon
          ? `${order.coupon?.coupon?.name} #${order.coupon?.shortId || ""}`
          : "-"}
      </TableCell>
      <TableCell className="font-semibold text-right font-mono tabular-nums">
        {pricing.ticketPrice}
      </TableCell>
      <TableCell className="font-semibold text-right font-mono tabular-nums">
        {pricing.serviceFee}
      </TableCell>
      <TableCell className="font-semibold text-right font-mono tabular-nums">
        {pricing.preTaxValue}
      </TableCell>
      <TableCell className="font-semibold text-right font-mono tabular-nums">
        {pricing.tax}
      </TableCell>
      <TableCell className="font-semibold text-right font-mono tabular-nums">
        {pricing.totalPrice}
      </TableCell>
      <TableCell className="font-semibold text-right font-mono tabular-nums">
        {order.paymentChannel || "-"}
      </TableCell>
      <TableCell className="text-right">
        <p
          className={`font-semibold ${order.status === statuses.APPROVE
              ? "text-[rgb(59,176,101)]"
              : order.status === statuses.PAID
                ? "text-[#4DA1A9]"
                : order.status === statuses.PENDING
                  ? "text-[#FAB12F]"
                  : "text-[#c15b78]"
            }`}
        >
          {constant.statusesTranslate[order.status] || order.status}
        </p>
      </TableCell>
    </TableRow>
  );
};
